import { useState } from "react";
import intl from "react-intl-universal";
import { CROSSBORDER_TOKENS, PAYOUT_NETWORKS, formatCrossborderAmount } from "../constants";
import { CrossborderKycStatusBadge } from "./CrossborderKycStatusBadge";

const ReadOnlyField = ({ label, value }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</label>
    <p className="mt-1 text-sm text-brand-dark dark:text-gray-100">{value || "—"}</p>
  </div>
);

const LimitCard = ({ label, value, currencyId }) => (
  <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
    <p className="mt-1 text-lg font-bold text-brand-dark dark:text-gray-100">
      {formatCrossborderAmount(value, currencyId)}
    </p>
  </div>
);

export const CrossborderCustomerProfileTab = ({ customer, profile, onStartKyc, canMutate = true }) => {
  const limits = profile?.limits;
  const address = profile?.address;
  const canStartKyc =
    canMutate && (customer.kycStatus === "pending" || customer.kycStatus === "rejected");

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-brand-dark-border dark:bg-brand-dark-elevated/20">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-brand-dark dark:text-gray-100">
                {intl.get("crossborder.kyc.statusTitle")}
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {intl.get("crossborder.kyc.statusDescription")}
              </p>
            </div>
            <CrossborderKycStatusBadge status={customer.kycStatus} />
          </div>
          {customer.kycStartedAt || customer.createdAt ? (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("crossborder.kyc.startedOn")}:{" "}
              <span className="font-medium text-brand-dark dark:text-gray-200">
                {customer.kycStartedAt ?? customer.createdAt}
              </span>
            </p>
          ) : null}
          {customer.kycApprovedAt ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("crossborder.kyc.approvedAt")}:{" "}
              <span className="font-medium text-brand-dark dark:text-gray-200">{customer.kycApprovedAt}</span>
            </p>
          ) : null}
          {canStartKyc ? (
            <button type="button" className="btn-primary mt-3 !h-9 !min-h-9 !px-4 text-xs" onClick={onStartKyc}>
              {intl.get("crossborder.kyc.startKyc")}
            </button>
          ) : null}
        </section>

        {limits ? (
          <section className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-brand-dark-border dark:bg-brand-dark-elevated/20">
            <h3 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">
              {intl.get("crossborder.kyc.limitsTitle")}
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <LimitCard
                label={intl.get("crossborder.kyc.limitPerTransaction")}
                value={limits.perTransaction}
                currencyId={limits.currencyId}
              />
              <LimitCard
                label={intl.get("crossborder.kyc.limitDaily")}
                value={limits.daily}
                currencyId={limits.currencyId}
              />
              <LimitCard
                label={intl.get("crossborder.kyc.limitMonthly")}
                value={limits.monthly}
                currencyId={limits.currencyId}
              />
            </div>
          </section>
        ) : null}
      </div>

      {profile ? (
        <>
          <section className="rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border">
            <h3 className="mb-4 text-sm font-bold text-brand-dark dark:text-gray-100">
              {intl.get("crossborder.kyc.complianceData")}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ReadOnlyField label={intl.get("crossborder.kyc.legalName")} value={profile.legalName} />
              <ReadOnlyField label={intl.get("crossborder.kyc.dba")} value={profile.dba} />
              <ReadOnlyField label={intl.get("crossborder.kyc.formationDate")} value={profile.formationDate} />
              <ReadOnlyField label={intl.get("crossborder.kyc.email")} value={profile.email} />
              <ReadOnlyField label={intl.get("crossborder.kyc.taxId")} value={profile.taxId} />
              <ReadOnlyField label={intl.get("crossborder.kyc.phone")} value={profile.phone} />
              <ReadOnlyField label={intl.get("crossborder.kyc.website")} value={profile.website} />
            </div>
          </section>

          {address ? (
            <section className="rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border">
              <h3 className="mb-4 text-sm font-bold text-brand-dark dark:text-gray-100">
                {intl.get("crossborder.kyc.physicalAddress")}
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ReadOnlyField label={intl.get("crossborder.kyc.street")} value={address.street} />
                <ReadOnlyField label={intl.get("crossborder.kyc.city")} value={address.city} />
                <ReadOnlyField label={intl.get("crossborder.kyc.state")} value={address.state} />
                <ReadOnlyField label={intl.get("crossborder.kyc.zipCode")} value={address.zipCode} />
                <ReadOnlyField label={intl.get("crossborder.kyc.country")} value={address.country} />
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <div className="app-empty-state">{intl.get("crossborder.kyc.noProfileData")}</div>
      )}
    </div>
  );
};

const RAIL_TABS = ["wire", "ach", "swift"];

const selectClass =
  "h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-elevated";

export const CrossborderVirtualAccountTab = ({ profile }) => {
  const account = profile?.virtualAccounts?.[0];
  const [activeRail, setActiveRail] = useState("wire");
  const [networkId, setNetworkId] = useState(account?.network?.toLowerCase() ?? "ethereum");
  const [tokenId, setTokenId] = useState(account?.token ?? "usdc");
  const [saved, setSaved] = useState(false);

  if (!account) {
    return <div className="app-empty-state">{intl.get("crossborder.customer.noVirtualAccounts")}</div>;
  }

  const railData = account[activeRail] ?? {};
  const selectedNetwork = PAYOUT_NETWORKS.find((n) => n.id === networkId)?.label ?? account.network;
  const selectedToken = CROSSBORDER_TOKENS.find((t) => t.id === tokenId)?.label ?? account.token?.toUpperCase();

  const handleSave = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border">
        <h3 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">
          {intl.get("crossborder.customer.walletConfig")}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {intl.get("crossborder.detail.blockchain")}
            </label>
            <select className={selectClass} value={networkId} onChange={(e) => setNetworkId(e.target.value)}>
              {PAYOUT_NETWORKS.map((network) => (
                <option key={network.id} value={network.id}>
                  {network.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {intl.get("crossborder.customer.token")}
            </label>
            <select className={selectClass} value={tokenId} onChange={(e) => setTokenId(e.target.value)}>
              {CROSSBORDER_TOKENS.map((token) => (
                <option key={token.id} value={token.id}>
                  {token.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          {canMutate ? (
            <button type="button" className="btn-primary !h-9 !min-h-9 !px-4 text-xs" onClick={handleSave}>
              {intl.get("crossborder.customer.save")}
            </button>
          ) : null}
          {saved ? (
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              {intl.get("crossborder.customer.saved")}
            </span>
          ) : null}
        </div>
        <p className="mt-2 text-xs text-gray-400">
          {selectedNetwork} · {selectedToken}
        </p>
      </section>

      <section className="rounded-xl border border-gray-100 p-4 dark:border-brand-dark-border">
        <h3 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">
          {intl.get("crossborder.customer.accountDetails")}
        </h3>
        <div className="mb-4 flex flex-wrap gap-2">
          {RAIL_TABS.map((rail) => (
            <button
              key={rail}
              type="button"
              onClick={() => setActiveRail(rail)}
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase transition ${
                activeRail === rail
                  ? "bg-brand-cream text-[#9a7340] dark:bg-brand-dark-elevated dark:text-brand-gold-mid"
                  : "bg-gray-100 text-gray-500 dark:bg-brand-dark-elevated dark:text-gray-400"
              }`}
            >
              {rail}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ReadOnlyField label={intl.get("crossborder.customer.bankName")} value={railData.bankName} />
          <ReadOnlyField label={intl.get("crossborder.customer.accountNumber")} value={railData.accountNumber} />
          <ReadOnlyField label={intl.get("crossborder.customer.routingNumber")} value={railData.routingNumber} />
          <ReadOnlyField label="IBAN" value={railData.iban} />
          <ReadOnlyField label="BIC/SWIFT" value={railData.bic} />
        </div>
      </section>
    </div>
  );
};

const PlaceholderTab = ({ titleKey }) => (
  <div className="app-empty-state rounded-xl border border-dashed border-gray-200 p-8 dark:border-brand-dark-border">
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{intl.get(titleKey)}</p>
    <p className="mt-1 text-xs text-gray-400">{intl.get("nav.soon")}</p>
  </div>
);

export const CrossborderCustomerTabContent = ({
  activeTab,
  customer,
  profile,
  onStartKyc,
  canMutate = true,
}) => {
  switch (activeTab) {
    case "profile":
      return (
        <CrossborderCustomerProfileTab
          customer={customer}
          profile={profile}
          onStartKyc={onStartKyc}
          canMutate={canMutate}
        />
      );
    case "virtualAccounts":
      return <CrossborderVirtualAccountTab profile={profile} />;
    case "wallets":
      return <PlaceholderTab titleKey="crossborder.customer.tabs.wallets" />;
    case "bankAccounts":
      return <PlaceholderTab titleKey="crossborder.customer.tabs.bankAccounts" />;
    case "transactions":
      return <PlaceholderTab titleKey="crossborder.customer.tabs.transactions" />;
    case "settings":
      return <PlaceholderTab titleKey="crossborder.customer.tabs.settings" />;
    default:
      return null;
  }
};
